import { ballRadiusRatios } from '@photo-merge/shared';
import { placeImage } from './crop';
import type { Photo } from './photos';

/**
 * Turns cropped photos into the square images the game renderer draws.
 *
 * The renderer clips to a circle itself and draws the image across that circle's
 * bounding box, so an export is simply the crop rendered into a square — no circular
 * masking here, which also keeps the edges crisp at any on-screen size.
 */

/** Largest export, for the biggest ball. Beyond this is wasted bytes on a phone. */
const MAX_EXPORT = 512;
const MIN_EXPORT = 128;

/** Export resolution for a rank, proportional to how large that ball actually draws. */
export function exportSizeFor(rank: number, count: number): number {
  const ratios = ballRadiusRatios(count);
  const relative = (ratios[rank - 1] ?? 1) / (ratios.at(-1) ?? 1);
  return Math.round(Math.max(MIN_EXPORT, MAX_EXPORT * relative));
}

export function renderBall(photo: Photo, size: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  ctx.imageSmoothingQuality = 'high';
  const p = placeImage(photo.width, photo.height, size, photo.crop);
  ctx.drawImage(photo.image, p.x, p.y, p.width, p.height);
  return canvas;
}

/** Renders the whole set, keyed by rank, ready to hand to the game renderer. */
export function renderBallSet(photos: Photo[]): Map<number, CanvasImageSource> {
  const images = new Map<number, CanvasImageSource>();
  photos.forEach((photo, index) => {
    const rank = index + 1;
    images.set(rank, renderBall(photo, exportSizeFor(rank, photos.length)));
  });
  return images;
}

/**
 * Encodes for upload. WebP is a third the size of JPEG at this quality and is
 * supported everywhere the rest of this app works; the fallback is only for very old
 * Safari, where toBlob quietly returns PNG instead.
 */
export function toUploadBlob(canvas: HTMLCanvasElement, quality = 0.85): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Could not encode image'))),
      'image/webp',
      quality,
    );
  });
}
